import { ProductClassificationEnum } from '@/enums/ProductClassificationEnum'

import type { Currency } from '@/config/tenants/types'
import type { Product } from '@/gen/ecom'
import type { ProductPricesRegistry } from '@/providers/PriceContextProvider'
import type { GTMItemList } from '@/types/gtm'

const mapGa4Categories = (categories?: Product['categories']) => ({
  item_category: categories?.[0]?.name ?? null,
  item_category2: categories?.[1]?.name ?? null,
  item_category3: categories?.[2]?.name ?? null,
  item_category4: categories?.[3]?.name ?? null,
  item_category5: categories?.[4]?.name ?? null,
})

const getClassificationValue = (classifications: Product['mediqClassifications'], code: string) =>
  classifications?.find((classification) => classification?.code === code)?.values?.find(Boolean) ?? null

/** Fields identifying a product that are shared across every GA4 item — add_to_cart, purchase, etc.
 * Keeping this in one place stops the events from drifting apart when a field is added or fixed. */
const mapProductIdentityToGTM = (product?: Product | null) => ({
  item_id: product?.code ?? null,
  item_name: product?.name ?? null,
  item_brand:
    getClassificationValue(product?.mediqClassifications, ProductClassificationEnum.brand) ??
    product?.manufacturer ??
    null,
  item_variant: product?.sizeDescription ?? null,
  ...mapGa4Categories(product?.categories),
})

export const formatProductToGTM = (
  productEntry?: Product | null,
  priceData?: ProductPricesRegistry['priceData'] | null,
  quantity?: number,
  itemList?: GTMItemList,
  fallbackCurrency?: Currency
) => {
  if (!productEntry) {
    return null
  }

  return {
    currency: priceData?.currencyIso || fallbackCurrency || null,
    ...mapProductIdentityToGTM(productEntry),
    discount: priceData?.discountValue ?? 0,
    item_list_id: itemList?.item_list_id ?? null,
    item_list_name: itemList?.item_list_name ?? null,
    price: priceData?.value ?? null,
    quantity: quantity ?? 1,
  }
}
